import { usePrevious } from '@react-hookz/web';
import { useBreakpointDown } from '@travel-frontend/core';
import { addDays, differenceInDays, formatISO, isEqual as isDateEqual } from 'date-fns';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import * as ActionTypes from '../../../../../components/SearchForm/components/TravelPeriodField/actionTypes.js';
import { useWidgetOverlay } from '../../../../../components/SearchForm/hooks/useWidgetOverlay.js';
export const useTravelPeriodField = ({
  value: controlledValue,
  config: {
    required,
    defaultDuration,
    minDate,
    maxDate,
    durationOptions,
    durationOptionsNew,
    holidayDates,
    durationLimit,
    showHolidays,
    withoutDuration
  },
  floatingOptions,
  invalid = false,
  disabled = false,
  dirty = false,
  layout = 'full',
  dimming = false,
  dispatch
}) => {
  const mdDown = useBreakpointDown('md');
  const [value, setValue] = useState(controlledValue);
  const widgetType = mdDown ? 'drawer' : 'popover';
  const [widgetOpen, setWidgetOpen] = useState(false);
  const [rangePending, setRangePending] = useState(false);
  const previousDefaultDuration = usePrevious(defaultDuration);
  const isDefaultDurationEqual = isEqual(defaultDuration, previousDefaultDuration);
  // update default duration on change package tour or own arrival
  useEffect(() => {
    setValue(controlledValue);
  }, [isDefaultDurationEqual]);
  const handleOpenChange = (open, origin) => {
    if (origin === 'close-button' && didValueChange()) {
      dispatchEvent({
        type: ActionTypes.CloseClicked,
        value: controlledValue
      });
      setValue(controlledValue);
    }
    if (didValueChange() && (origin === 'apply-button' || !open && widgetType === 'popover')) {
      const valueSanitized = sanitizeValueIfNecessary(value);
      // @FIXME only when !isDefaultPrevented?
      setValue(valueSanitized);
      // eslint-disable-next-line no-var
      var {
        isDefaultPrevented
      } = dispatchEvent({
        type: ActionTypes.ValueChanged,
        value: valueSanitized
      });
    }
    // @ts-expect-error FIXME
    if (!isDefaultPrevented) {
      if (!open) {
        setRangePending(false);
      }
      setWidgetOpen(open);
    }
  };
  const overlay = useWidgetOverlay({
    open: widgetOpen,
    type: widgetType,
    options: floatingOptions,
    onOpenChange: handleOpenChange,
    layout,
    dimming
  });
  const handleApply = () => {
    handleOpenChange(false, 'apply-button');
  };
  const handleClose = () => {
    handleOpenChange(false, 'close-button');
  };
  const handleDurationChange = duration => {
    const newValue = {
      startDate: value?.startDate,
      endDate: value?.endDate,
      duration
    };
    dispatchEvent({
      type: ActionTypes.DurationChanged,
      value: newValue
    });
    setValue(newValue);
  };
  const handleRangeChange = range => {
    const startDate = range.start ? formatISO(range.start, {
      representation: 'date'
    }) : null;
    const endDate = range.end ? formatISO(range.end, {
      representation: 'date'
    }) : null;
    const newValue = {
      ...value,
      startDate,
      endDate,
      duration: null
    };
    if (!range.pending) {
      dispatchEvent({
        type: ActionTypes.RangeChanged,
        value: newValue
      });
    }
    setValue(newValue);
    setRangePending(range.pending);
  };
  const sanitizeValueIfNecessary = value => {
    if (!value) {
      return null;
    }
    const valueSanitized = {
      ...value
    };
    const startDateParsed = value.startDate ? new Date(value.startDate) : null;
    const endDateParsed = value.endDate ? new Date(value.endDate) : null;
    if (startDateParsed && endDateParsed) {
      if (isDateEqual(startDateParsed, endDateParsed)) {
        valueSanitized.endDate = formatISO(addDays(startDateParsed, defaultDuration.max), {
          representation: 'date'
        });
        valueSanitized.duration = {
          min: defaultDuration.min,
          max: defaultDuration.max
        };
      } else if (!value.duration) {
        const selectedDaysCount = differenceInDays(endDateParsed, startDateParsed);
        valueSanitized.duration = selectedDaysCount < defaultDuration.min ? {
          min: 0,
          max: 0
        } : {
          min: defaultDuration.min,
          max: defaultDuration.max
        };
      }
    }
    return valueSanitized;
  };
  const areValuesEqual = (valueA, valueB) => {
    return valueA?.startDate === valueB?.startDate && valueA?.endDate === valueB?.endDate && isEqual(valueA?.duration, valueB?.duration);
  };
  const didValueChange = () => {
    return !areValuesEqual(value, controlledValue);
  };
  const isValidValue = value => true;
  const dispatchEvent = ({
    type,
    value
  }) => {
    let isDefaultPrevented = false;
    dispatch({
      type,
      payload: {
        value,
        valid: isValidValue(value)
      },
      preventDefault: () => {
        isDefaultPrevented = true;
      }
    });
    return {
      isDefaultPrevented
    };
  };
  const getFieldProps = () => ({
    ...overlay.getReferenceProps(),
    value,
    active: widgetOpen,
    invalid,
    disabled
  });
  const getWidgetProps = () => ({
    ...overlay.getWidgetProps(),
    type: widgetType,
    open: widgetOpen,
    data: value,
    rangePending,
    handlers: {
      onApply: handleApply,
      onClose: handleClose,
      onDurationChange: handleDurationChange,
      onRangeChange: handleRangeChange
    },
    config: {
      defaultDuration,
      minDate,
      maxDate,
      durationOptions,
      durationOptionsNew,
      holidayDates,
      durationLimit,
      showHolidays
    },
    layout,
    withoutDuration,
    dimming
  });
  const setValueHandler = nextValue => {
    if (!areValuesEqual(value, nextValue)) {
      dispatchEvent({
        type: ActionTypes.FunctionValueChanged,
        value: nextValue
      });
      setValue(nextValue);
    }
  };
  const checkValidity = () => required === false || isValidValue(value);
  return {
    isActive: widgetOpen,
    isInvalid: invalid,
    isDisabled: disabled,
    getFieldProps,
    getWidgetProps,
    setValue: setValueHandler,
    checkValidity
  };
};